import { lazy, Suspense, ComponentType } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { LanguageLayout } from "@/components/LanguageLayout";
import { LanguageRedirect } from "@/components/LanguageRedirect";
import Index from "./pages/Index";

// Lazy-load TooltipProvider — defers @floating-ui from critical bundle
const LazyTooltipProvider = lazy(() =>
  import("@/components/ui/tooltip").then(m => ({ default: m.TooltipProvider }))
);

// Retry wrapper for lazy imports — handles stale chunk errors after redeploys
function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch(() => {
      // Force reload on stale chunk error (once)
      const key = "chunk-retry";
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
      }
      return factory();
    })
  );
}

// Lazy-load toast components — not needed at initial render
const Toaster = lazyRetry(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster as ComponentType<any> })));
const Sonner = lazyRetry(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster as ComponentType<any> })));

// Lazy-load all non-homepage routes
const NotFound = lazy(() => import("./pages/NotFound"));
const Features = lazy(() => import("./pages/Features"));
const FeatureDetail = lazy(() => import("./pages/FeatureDetail"));
const About = lazy(() => import("./pages/About"));
const Demo = lazy(() => import("./pages/Demo"));
const Services = lazy(() => import("./pages/Services"));
const Charter = lazy(() => import("./pages/Charter"));
const Management = lazy(() => import("./pages/Management"));
const Sales = lazy(() => import("./pages/Sales"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Contact = lazy(() => import("./pages/Contact"));
const Careers = lazy(() => import("./pages/Careers"));
const AdminSubmissions = lazy(() => import("./pages/AdminSubmissions"));
const AdminNews = lazy(() => import("./pages/AdminNews"));
const AdminIndex = lazy(() => import("./pages/AdminIndex"));
const AdminAssets = lazy(() => import("./pages/AdminAssets"));


const Solutions = lazy(() => import("./pages/Solutions"));
const SolutionDetail = lazy(() => import("./pages/SolutionDetail"));
const WhyUs = lazy(() => import("./pages/WhyUs"));
const WhyUsDetail = lazy(() => import("./pages/WhyUsDetail"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const Press = lazy(() => import("./pages/Press"));
const SeoLanding = lazy(() => import("./pages/SeoLanding"));
const News = lazy(() => import("./pages/News"));
const NewsArticleDetail = lazy(() => import("./pages/NewsArticleDetail"));
const NewsArchive = lazy(() => import("./pages/NewsArchive"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

/* ── Legacy redirect helpers for parameterized old routes ── */
const LegacyRedirect = ({ to }: { to: string }) => {
  const { lang } = useParams();
  return <Navigate to={`/${lang}/${to}`} replace />;
};

const FeatureSlugRedirect = () => {
  const { lang, slug } = useParams();
  return <Navigate to={`/${lang}/yacht-platform-features/${slug}`} replace />;
};

const SolutionSlugRedirect = () => {
  const { lang, slug } = useParams();
  const slugMap: Record<string, string> = {
    "charter-brokers": "charter-broker-software",
    "yacht-brokers": "yacht-broker-software",
    "yacht-managers": "yacht-manager-software",
  };
  return <Navigate to={`/${lang}/yacht-industry-solutions/${slugMap[slug || ""] || slug}`} replace />;
};

const WhyUsSlugRedirect = () => {
  const { lang, slug } = useParams();
  const slugMap: Record<string, string> = {
    "all-in-one": "all-in-one-yacht-platform",
    "built-for-professionals": "built-for-yacht-professionals",
    "security": "yacht-data-security",
    "mobile": "mobile-yacht-management",
  };
  return <Navigate to={`/${lang}/why-seaventis/${slugMap[slug || ""] || slug}`} replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Suspense fallback={null}>
      <LazyTooltipProvider>
        <Suspense fallback={null}>
          <Toaster />
          <Sonner />
        </Suspense>
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Root → auto-detect language and redirect */}
              <Route path="/" element={<LanguageRedirect />} />

              {/* All routes under /:lang */}
              <Route path="/:lang" element={<LanguageLayout />}>
                <Route index element={<Index />} />

                {/* Primary SEO-optimized routes */}
                <Route path="yacht-platform-features" element={<Features />} />
                <Route path="yacht-platform-features/:slug" element={<FeatureDetail />} />
                <Route path="yacht-software-modules" element={<Features />} />
                <Route path="yacht-charter-management-software" element={<Charter />} />
                <Route path="yacht-management-software" element={<Management />} />
                <Route path="yacht-brokerage-software" element={<Sales />} />
                <Route path="yacht-management-services" element={<Services />} />
                <Route path="about" element={<About />} />
                <Route path="book-demo" element={<Demo />} />
                <Route path="contact" element={<Contact />} />
                <Route path="careers" element={<Careers />} />
                <Route path="privacy-policy" element={<PrivacyPolicy />} />
                <Route path="terms-of-service" element={<TermsOfService />} />
                <Route path="yacht-industry-solutions" element={<Solutions />} />
                <Route path="yacht-industry-solutions/:slug" element={<SolutionDetail />} />
                <Route path="why-seaventis" element={<WhyUs />} />
                <Route path="why-seaventis/:slug" element={<WhyUsDetail />} />
                <Route path="sitemap" element={<Sitemap />} />
                <Route path="press" element={<Press />} />
                <Route path="best-yacht-charter-software" element={<SeoLanding />} />
                <Route path="best-yacht-management-software" element={<SeoLanding />} />
                <Route path="best-yacht-sales-software" element={<SeoLanding />} />
                <Route path="yacht-software-for-captains" element={<SeoLanding />} />
                <Route path="yacht-inventory-management-software" element={<SeoLanding />} />
                <Route path="yacht-crew-management-payroll-software" element={<SeoLanding />} />
                <Route path="yacht-management-app" element={<SeoLanding />} />
                <Route path="yacht-accounting-banking-software" element={<SeoLanding />} />
                <Route path="yacht-digital-forms-checklists" element={<SeoLanding />} />
                <Route path="yacht-software-onboarding" element={<SeoLanding />} />
                <Route path="yacht-charter-crm" element={<SeoLanding />} />
                <Route path="yacht-charter-management-software-guide" element={<SeoLanding />} />
                <Route path="yacht-software-for-owners" element={<SeoLanding />} />
                <Route path="yacht-software-for-chief-engineers" element={<SeoLanding />} />
                <Route path="yacht-software-for-pursers-chief-stews" element={<SeoLanding />} />
                <Route path="yacht-software-for-heads-of-department" element={<SeoLanding />} />
                <Route path="yacht-industry-news" element={<News />} />
                <Route path="yacht-industry-news/archive" element={<NewsArchive />} />
                <Route path="yacht-industry-news/:slug" element={<NewsArticleDetail />} />
                <Route path="admin" element={<AdminIndex />} />
                <Route path="admin/submissions" element={<AdminSubmissions />} />
                <Route path="admin/news" element={<AdminNews />} />
                <Route path="admin/assets" element={<AdminAssets />} />
                
                

                {/* Legacy 301 redirects from old URLs */}
                <Route path="features" element={<LegacyRedirect to="yacht-platform-features" />} />
                <Route path="features/:slug" element={<FeatureSlugRedirect />} />
                <Route path="modules" element={<LegacyRedirect to="yacht-software-modules" />} />
                <Route path="charter" element={<LegacyRedirect to="yacht-charter-management-software" />} />
                <Route path="products/charter" element={<LegacyRedirect to="yacht-charter-management-software" />} />
                <Route path="products/management" element={<LegacyRedirect to="yacht-management-software" />} />
                <Route path="products/sales" element={<LegacyRedirect to="yacht-brokerage-software" />} />
                <Route path="services" element={<LegacyRedirect to="yacht-management-services" />} />
                <Route path="demo" element={<LegacyRedirect to="book-demo" />} />
                <Route path="privacy" element={<LegacyRedirect to="privacy-policy" />} />
                <Route path="terms" element={<LegacyRedirect to="terms-of-service" />} />
                <Route path="solutions" element={<LegacyRedirect to="yacht-industry-solutions" />} />
                <Route path="solutions/:slug" element={<SolutionSlugRedirect />} />
                <Route path="why-us" element={<LegacyRedirect to="why-seaventis" />} />
                <Route path="why-us/:slug" element={<WhyUsSlugRedirect />} />

                <Route path="*" element={<NotFound />} />
              </Route>

              {/* Legacy unprefixed paths → redirect to /en/... */}
              <Route path="*" element={<LanguageRedirect />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </LazyTooltipProvider>
    </Suspense>
  </QueryClientProvider>
);

export default App;
